import { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../lib/api';

const asArray = (value) => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.leaderboard)) return value.leaderboard;
  if (Array.isArray(value?.validators)) return value.validators;
  if (Array.isArray(value?.nodes)) return value.nodes;
  if (Array.isArray(value?.data)) return value.data;
  return [];
};

const normalizeAddress = (value) => `${value || ''}`.trim().toLowerCase();

const pickValue = (source, keys, fallback = '—') => {
  for (const key of keys) {
    const value = key.split('.').reduce((acc, part) => acc?.[part], source);
    if (value !== undefined && value !== null && value !== '') {
      return value;
    }
  }
  return fallback;
};

const pickNodeForAddress = (payload, address) => {
  const wanted = normalizeAddress(address);
  if (!wanted || !payload || typeof payload !== 'object') return null;

  const directCandidates = [
    payload.node,
    payload.nodeInfo,
    payload.validator,
    payload.data?.node,
    payload.data?.validator,
  ].filter(Boolean);

  const direct = directCandidates.find((candidate) => {
    const candidateAddress = normalizeAddress(
      candidate?.address || candidate?.wallet || candidate?.validator_address || candidate?.owner
    );
    return candidateAddress === wanted;
  });

  if (direct) return direct;

  return (
    asArray(payload).find((candidate) => {
      const candidateAddress = normalizeAddress(
        candidate?.address || candidate?.wallet || candidate?.validator_address || candidate?.owner
      );
      return candidateAddress === wanted;
    }) || null
  );
};

const buildNodeInfo = (payload, address) => {
  const matched = pickNodeForAddress(payload, address);
  const source = matched || payload || {};

  return {
    address: address || pickValue(source, ['address', 'wallet', 'validator_address', 'owner'], ''),
    name: pickValue(source, ['name', 'pseudo', 'alias', 'validator_name'], 'Wallet not registered'),
    status: pickValue(source, ['status', 'state', 'validator_status'], address ? 'Monitoring' : 'Disconnected'),
    stake: pickValue(source, ['stake', 'staked', 'total_stake', 'amount_staked'], '0'),
    rewards: pickValue(source, ['rewards', 'pending_rewards', 'earned'], '0'),
    apy: pickValue(source, ['apy', 'apr', 'estimated_apy'], 'Variable'),
    uptime: pickValue(source, ['uptime', 'availability'], 'Unknown'),
    commission: pickValue(source, ['commission', 'fee'], '—'),
  };
};

const buildLeaderboard = (payload) =>
  asArray(payload).map((entry, index) => ({
    rank: entry?.rank || index + 1,
    name:
      entry?.name ||
      entry?.pseudo ||
      entry?.alias ||
      entry?.validator_name ||
      entry?.address ||
      `Validator ${index + 1}`,
    address: entry?.address || entry?.wallet || entry?.validator_address || entry?.owner || '—',
    stake: entry?.stake || entry?.staked || entry?.total_stake || '0',
    rewards: entry?.rewards || entry?.pending_rewards || entry?.earned || '0',
    status: entry?.status || entry?.state || 'Active',
    uptime: entry?.uptime || entry?.availability || '—',
    apy: entry?.apy || entry?.apr || '—',
  }));

export const useStaking = (address) => {
  const [nodeInfo, setNodeInfo] = useState(() => buildNodeInfo(null, address));
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const refetch = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const response = await api.staking.info();
      setNodeInfo(buildNodeInfo(response, address));
      setLeaderboard(buildLeaderboard(response));
      return response;
    } catch (err) {
      const message = err?.response?.data?.error || err?.message || 'Unable to load staking data.';
      setError(message);
      setNodeInfo(buildNodeInfo(null, address));
      setLeaderboard([]);
      return null;
    } finally {
      setLoading(false);
    }
  }, [address]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  return useMemo(
    () => ({
      nodeInfo,
      leaderboard,
      loading,
      error,
      refetch,
    }),
    [nodeInfo, leaderboard, loading, error, refetch]
  );
};

export default useStaking;